import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryKeys } from '@/api/query-client';
import type { paths } from '@/api/generated/schema';

type ConversationsQuery = NonNullable<
  paths['/api/v1/admin/conversations'] extends { get: { parameters: { query?: infer TQuery } } }
    ? TQuery
    : never
>;

/** Liste paginée des conversations support, triée par activité récente (vue admin). */
export function useConversations(params: ConversationsQuery = {}) {
  return useQuery({
    queryKey: queryKeys.conversations(params),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations', { params: { query: params } });

      return data!;
    },
  });
}

/** Détail de la conversation support d'un tenant (fil de messages). */
export function useConversationDetail(tenantId: string) {
  return useQuery({
    queryKey: queryKeys.conversation(tenantId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations/{tenantId}', {
        params: { path: { tenantId } },
      });

      return data!;
    },

    enabled: tenantId.length > 0,
  });
}

type ArchivedConversationsQuery = NonNullable<
  paths['/api/v1/admin/conversations/{tenantId}/archive'] extends { get: { parameters: { query?: infer TQuery } } }
    ? TQuery
    : never
>;

/** Liste paginée des conversations support fermées (archive) d'un tenant, triée par fermeture récente. */
export function useArchivedConversations(tenantId: string, page: number, pageSize: number) {
  return useQuery({
    queryKey: queryKeys.archivedConversations(tenantId, { page, pageSize }),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations/{tenantId}/archive', {
        params: { path: { tenantId }, query: { page, pageSize } satisfies ArchivedConversationsQuery },
      });

      return data!;
    },

    enabled: tenantId.length > 0,
  });
}

/** Détail complet (fil de messages) d'une conversation support archivée, par id. */
export function useArchivedConversation(conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.archivedConversation(conversationId ?? ''),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations/archive/{conversationId}', {
        params: { path: { conversationId: conversationId! } },
      });

      return data!;
    },

    enabled: conversationId != null && conversationId.length > 0,
  });
}

/** Réponse d'un admin à un tenant — crée la conversation si elle n'existe pas encore. */
type AdminReplyBody = NonNullable<
  paths['/api/v1/admin/conversations/{tenantId}/messages'] extends {
    post: { requestBody: { content: { 'application/json': infer TBody } } };
  }
    ? TBody
    : never
>;

export function useAdminReply(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminReplyBody) => {
      const { data } = await api.POST('/api/v1/admin/conversations/{tenantId}/messages', {
        params: { path: { tenantId } },
        body,
      });

      return data!;
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) }),
      ]);
    },
  });
}

/** Émet un ticket de dépôt présigné pour joindre un fichier à une réponse admin. */
export function useCreateAdminAttachmentUploadTicket(tenantId: string) {
  return useMutation({
    mutationFn: async (body: { fileName: string; contentType: string; sizeBytes: number }) => {
      const { data } = await api.POST('/api/v1/admin/conversations/{tenantId}/attachments/upload-ticket', {
        params: { path: { tenantId } },
        body,
      });

      return data!;
    },
  });
}

/** Émet une URL de téléchargement présignée pour la pièce jointe d'un message. */
export function useAdminMessageAttachmentDownloadUrl() {
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { data } = await api.POST('/api/v1/admin/conversations/messages/{messageId}/attachment/download-url', {
        params: { path: { messageId } },
        // Voir le commentaire sur `useMarkConversationReadAsAdmin` : corps vide nécessaire au
        // niveau transport même si le contrat OpenAPI n'en déclare aucun.
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });

      return data!;
    },
  });
}

/** Ferme la conversation support d'un tenant. Définitif : voir `useStartNewConversation`. */
export function useCloseConversation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/admin/conversations/{tenantId}/close', {
        params: { path: { tenantId } },
        // Voir le commentaire sur `useMarkConversationReadAsAdmin` : corps vide nécessaire au
        // niveau transport même si le contrat OpenAPI ne déclare aucun corps pour cette route.
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) }),
      ]);
    },
  });
}

/**
 * Démarre explicitement une nouvelle conversation pour un tenant, après la fermeture de la
 * précédente. Refusé par le backend si une conversation est déjà en cours (non fermée).
 */
export function useStartNewConversation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/admin/conversations/{tenantId}/new', {
        params: { path: { tenantId } },
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });

      return data!;
    },

    onSettled: async () => {
      // Y compris en échec (409 « conversation.already_open ») : le refus signifie qu'une
      // conversation ouverte existe déjà côté serveur — la vue doit se resynchroniser sur cet
      // état réel plutôt que de rester bloquée sur un badge « fermée » périmé.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) }),
      ]);
    },
  });
}

type ArchivedConversationsAcrossTenantsQuery = NonNullable<
  paths['/api/v1/admin/conversations/archive'] extends { get: { parameters: { query?: infer TQuery } } }
    ? TQuery
    : never
>;

/** Liste paginée de TOUTES les conversations fermées de la plateforme (tous tenants), triée par fermeture récente. */
export function useArchivedConversationsAcrossTenants(page: number, pageSize: number) {
  return useQuery({
    queryKey: queryKeys.archivedConversationsAcrossTenants({ page, pageSize }),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations/archive', {
        params: { query: { page, pageSize } satisfies ArchivedConversationsAcrossTenantsQuery },
      });

      return data!;
    },
  });
}

/** Assigne (ou désassigne, si `adminUserId` est `null`) la conversation d'un tenant à un administrateur. */
export function useAssignConversation(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (adminUserId: string | null) => {
      await api.POST('/api/v1/admin/conversations/{tenantId}/assign', {
        params: { path: { tenantId } },
        body: { adminUserId },
      });
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) }),
      ]);
    },
  });
}

/** Journal d'audit du cycle de vie de la conversation support d'un tenant (démarrage, fermeture, (dés)assignation). */
export function useConversationAuditLog(tenantId: string) {
  return useQuery({
    queryKey: queryKeys.conversationAuditLog(tenantId),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/admin/conversations/{tenantId}/audit', {
        params: { path: { tenantId } },
      });

      return data!;
    },

    enabled: tenantId.length > 0,
  });
}

/** Marque la conversation d'un tenant comme lue côté back-office. */
export function useMarkConversationReadAsAdmin(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/admin/conversations/{tenantId}/read', {
        params: { path: { tenantId } },
        // Voir le commentaire sur `useTenantLifecycleMutation` dans `platform-admin.ts` :
        // corps vide nécessaire au niveau transport même si le contrat OpenAPI n'en déclare
        // aucun (FastEndpoints répond 415 sans `Content-Type` + corps JSON valide).
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });
    },

    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations() }),
        queryClient.invalidateQueries({ queryKey: queryKeys.conversation(tenantId) }),
      ]);
    },
  });
}
